import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import type { ChecklistItem } from "@/lib/db-types";
import type { ProjectClosingValidation } from "@/hooks/use-project-closing";
import { SrIcon } from "@/components/ui/SrIcon";
import { DataChecklistRow, ManualChecklistRow, BfReviewBlock } from "./ClosingChecklistRows";
import { ClosingStorageSection, type StorageCaptureState } from "./ClosingStorageSection";

const CHECKLIST_KEYS = {
  TASKS: "tasks_approved",
  VOUCHERS: "vouchers_approved",
  FINAL_INVOICE: "final_invoice_sent",
  BF: "bf_approved",
  INVOICE_DIFF: "invoice_diff_confirmed",
  CLEAN_422: "clean_422_levert",
} as const;

export const DEFAULT_CLOSING_CHECKLIST: ChecklistItem[] = [
  { key: CHECKLIST_KEYS.TASKS, label: "Alle oppgaver godkjent", checked: false },
  { key: CHECKLIST_KEYS.VOUCHERS, label: "Alle bilag godkjent", checked: false },
  { key: CHECKLIST_KEYS.FINAL_INVOICE, label: "Sluttfaktura sendt", checked: false },
  { key: CHECKLIST_KEYS.BF, label: "GEEK-kalkulering godkjent", checked: false },
  { key: CHECKLIST_KEYS.CLEAN_422, label: "Clean 4:2:2 levert", checked: false },
];

/** VIB-1308: lagringsdata fanget i lukkedialogen (frivillig). */
export interface ClosingStoragePayload {
  storage_gb: number;
  storage_location: string | null;
}

function getItem(checklist: ChecklistItem[], key: string): ChecklistItem | undefined {
  return checklist.find((i) => i.key === key);
}

function upsertItem(checklist: ChecklistItem[], item: ChecklistItem): ChecklistItem[] {
  const exists = checklist.some((i) => i.key === item.key);
  return exists
    ? checklist.map((i) => (i.key === item.key ? item : i))
    : [...checklist, item];
}

function removeItem(checklist: ChecklistItem[], key: string): ChecklistItem[] {
  return checklist.filter((i) => i.key !== key);
}

export function ClosingChecklistDialog({
  open,
  onOpenChange,
  checklist,
  onChecklistChange,
  onConfirm,
  projectId,
  validation,
  adminOverride = false,
  hasCustomer = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklist: ChecklistItem[];
  onChecklistChange: (checklist: ChecklistItem[]) => void;
  /** VIB-1308: storage-payload sendes med når GB er fylt ut. */
  onConfirm: (storage?: ClosingStoragePayload) => void;
  projectId: string;
  validation: ProjectClosingValidation;
  /**
   * Admin/superadmin kan tvinge gjennom lukking selv om data-blockers
   * (oppgaver, bilag, sluttfaktura) ikke er ryddet. Erik må ha denne
   * muligheten på prosjekter uten fakturering eller med spesialhåndtering.
   * BF-kalkulering må fortsatt bekreftes så admin ikke ved et uhell klikker
   * forbi noe. Clean 4:2:2 er frivillig og blokkerer ikke.
   */
  adminOverride?: boolean;
  /** VIB-1308: har prosjektet en kunde? (Storage-avtale krever kunde.) */
  hasCustomer?: boolean;
}) {
  const navigate = useNavigate();
  const items = checklist.length > 0 ? checklist : DEFAULT_CLOSING_CHECKLIST;
  const [storage, setStorage] = useState<StorageCaptureState>({ gb: "", location: "" });
  // Radix fokuserer ellers første tabbable element: «Gå til faktura»-lenken i
  // varselboksen, så en Enter-refleks navigerte bort midt i lukkeflyten (VIB-2529).
  const bfCheckboxRef = useRef<HTMLButtonElement>(null);

  const bfApproved = getItem(items, CHECKLIST_KEYS.BF)?.checked ?? false;
  const invoiceDiffConfirmed = getItem(items, CHECKLIST_KEYS.INVOICE_DIFF)?.checked ?? false;
  const clean422Levert = getItem(items, CHECKLIST_KEYS.CLEAN_422)?.checked ?? false;

  const dataItems = [
    {
      key: CHECKLIST_KEYS.TASKS,
      label: "Alle oppgaver godkjent",
      blocked: validation.blockers.tasks,
      blockedMsg: `${validation.openTasks} ${validation.openTasks === 1 ? "oppgave gjenstår" : "oppgaver gjenstår"}`,
    },
    {
      key: CHECKLIST_KEYS.VOUCHERS,
      label: "Alle bilag godkjent",
      blocked: validation.blockers.vouchers,
      blockedMsg: `${validation.pendingVouchers} ${validation.pendingVouchers === 1 ? "bilag" : "bilag"} ikke godkjent`,
    },
    {
      key: CHECKLIST_KEYS.FINAL_INVOICE,
      label: "Sluttfaktura sendt",
      blocked: validation.blockers.finalInvoice,
      blockedMsg: "Ingen sluttfaktura registrert",
      action: { label: "Gå til faktura", onClick: () => navigate(`/prosjekter/${projectId}/faktura`) },
    },
  ];

  // "Clean 4:2:2 levert" er FRIVILLIG (eneste valgfrie sjekkpunkt): den vises og
  // kan hukes av, men blokkerer IKKE lukking. Derfor ikke med i canConfirm.
  const canConfirm =
    (adminOverride ||
      (!validation.blockers.tasks &&
        !validation.blockers.vouchers &&
        !validation.blockers.finalInvoice)) &&
    bfApproved &&
    (!validation.hasInvoiceDiff || invoiceDiffConfirmed);

  const handleToggleBf = () => {
    const current = getItem(items, CHECKLIST_KEYS.BF);
    onChecklistChange(
      upsertItem(items, {
        key: CHECKLIST_KEYS.BF,
        label: "GEEK-kalkulering godkjent",
        checked: !(current?.checked ?? false),
      }),
    );
  };

  const handleToggleDiff = () => {
    const current = getItem(items, CHECKLIST_KEYS.INVOICE_DIFF);
    onChecklistChange(
      upsertItem(items, {
        key: CHECKLIST_KEYS.INVOICE_DIFF,
        label: "Avvik på fakturert beløp bekreftet",
        checked: !(current?.checked ?? false),
      }),
    );
  };

  const handleToggle422 = () => {
    const current = getItem(items, CHECKLIST_KEYS.CLEAN_422);
    onChecklistChange(
      upsertItem(items, {
        key: CHECKLIST_KEYS.CLEAN_422,
        label: "Clean 4:2:2 levert",
        checked: !(current?.checked ?? false),
      }),
    );
  };

  const handleConfirmClick = () => {
    // Sync data-driven items with validation state before saving
    let next = items;
    for (const di of dataItems) {
      next = upsertItem(next, { key: di.key, label: di.label, checked: !di.blocked });
    }
    // Clear invoice-diff item if no diff
    if (!validation.hasInvoiceDiff) {
      next = removeItem(next, CHECKLIST_KEYS.INVOICE_DIFF);
    }
    onChecklistChange(next);

    // VIB-1308: opprett storage-avtale kun når GB > 0 og prosjektet har kunde.
    const gb = Number(storage.gb);
    const storagePayload =
      gb > 0 && hasCustomer
        ? { storage_gb: gb, storage_location: storage.location.trim() || null }
        : undefined;
    onConfirm(storagePayload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          bfCheckboxRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Lukk prosjekt</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-4">
          Punktene under må være i orden før prosjektet kan settes som fullført.
          «Clean 4:2:2 levert» er frivillig.
        </p>

        {adminOverride && (
          <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <SrIcon name="alert-triangle" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Admin-overstyring: du kan lukke prosjektet selv om oppgaver, bilag eller sluttfaktura ikke er ryddet. Bruk forsiktig.
            </span>
          </div>
        )}

        <div className="space-y-2.5">
          {dataItems.map((di) => (
            <DataChecklistRow
              key={di.key}
              label={di.label}
              blocked={di.blocked}
              blockedMsg={di.blockedMsg}
              action={di.action}
            />
          ))}

          <ManualChecklistRow
            label="GEEK-kalkulering godkjent"
            checked={bfApproved}
            onToggle={handleToggleBf}
            checkboxRef={bfCheckboxRef}
          >
            <BfReviewBlock
              bfEstimated={validation.bfEstimated}
              bfActual={validation.bfActual}
              bfDiff={validation.bfDiff}
            />
          </ManualChecklistRow>

          <ManualChecklistRow
            label="Clean 4:2:2 levert (frivillig)"
            checked={clean422Levert}
            onToggle={handleToggle422}
          />

          <ClosingStorageSection value={storage} onChange={setStorage} hasCustomer={hasCustomer} />

          {validation.hasInvoiceDiff && (
            <ManualChecklistRow
              label={`Bekreft avvik på fakturert beløp (${formatNOK(Math.abs(validation.invoiceDiff))})`}
              checked={invoiceDiffConfirmed}
              onToggle={handleToggleDiff}
              variant="warning"
            >
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 rounded px-3 py-2 border border-amber-200">
                Fakturert {formatNOK(validation.invoicedTotal)} av kontraktssum {formatNOK(validation.contractAmount)}.
                Avvik: {formatNOK(validation.invoiceDiff)}.
              </div>
            </ManualChecklistRow>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button
            onClick={handleConfirmClick}
            disabled={!canConfirm}
            className={cn(!canConfirm && "opacity-50 cursor-not-allowed")}
          >
            <SrIcon name="check-circle2" className="w-4 h-4 mr-2" />
            Lukk prosjekt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ClosingChecklistDialog;
